import { createEffect, createMemo, createSignal, Show } from "solid-js";
import type { VoidComponent } from "solid-js";
import {
  boolean,
  check,
  forward,
  type InferOutput,
  maxLength,
  object,
  optional,
  partialCheck,
  picklist,
  pipe,
  string,
  trim,
} from "valibot";
import { keyFromSelector } from "i18next";
import pMinDelay from "p-min-delay";
import { HttpStatusCode } from "axios";
import { isEmpty } from "lodash-es";
import { useTranslation } from "../../../../../state/i18n";
import SettingSection from "../../components/SettingSection";
import { EditableSettingTextValue } from "../../components/SettingValue";
import SettingSectionEditButton from "../../components/SettingSectionEditButton";
import SettingSectionEditActions from "../../components/SettingSectionEditActions";
import EditableCountryValue from "./EditableCountryValue";
import EditablePhoneNumberValue from "./EditablePhoneNumberValue";
import { createForm } from "../../../../../components/form/createForm";
import { MAX_CHARACTERS_SM, MAX_CHARACTERS_XS, MINIMUM_ACTION_LOADING_DELAY } from "../../../../../constants";
import { errorTranslation, phoneNumber, requireText } from "../../../../../utils/validation/base";
import { isValidEmail } from "../../../../../utils/emailUtil";
import { isEuCountry } from "../../../../../utils/countryUtil";
import BillingService, {
  type BillingProfileDto,
  type BillingProfilePatchDto,
} from "../../../../../services/BillingService";
import CountriesService from "../../../../../services/CountriesService";
import { useModalManager } from "../../../../../components/modal/ModalManager";
import GenericFailureModal from "../../../../GenericFailureModal";
import { MoreAppError } from "../../../../../MoreAppError";

const SPANISH_STRIPE_ACCOUNT = "moreapp-es";
const INVALID_VAT_MESSAGE_PATTERN = /\bVAT\b/i;
const VAT_APPLICABILITIES = ["applicable", "notApplicable", "unknown"] as const;
type VatApplicability = (typeof VAT_APPLICABILITIES)[number];

const notEmptyMessage = () => errorTranslation(keyFromSelector(($) => $.VALIDATION_NOT_EMPTY));

const isSpanishStripeAccount = (stripeAccount: string): boolean => stripeAccount === SPANISH_STRIPE_ACCOUNT;

// Stripe can hold lower case country codes, while the countries are identified by upper case codes
const toCountryCode = (billingProfile: BillingProfileDto): string =>
  billingProfile.billingAddress.countryCode?.toUpperCase() ?? "";

const BillingInformationSchema = pipe(
  object({
    companyName: requireText(MAX_CHARACTERS_SM, true),
    email: pipe(
      requireText(MAX_CHARACTERS_SM, true),
      check(isValidEmail, errorTranslation(keyFromSelector(($) => $.VALIDATION_EMAIL))),
    ),
    phoneNumber: pipe(requireText(MAX_CHARACTERS_SM, true), phoneNumber()),
    address: requireText(MAX_CHARACTERS_SM, true),
    zipCode: requireText(MAX_CHARACTERS_XS, true),
    city: requireText(MAX_CHARACTERS_SM, true),
    countryCode: requireText(MAX_CHARACTERS_XS, true),
    vat: optional(
      pipe(
        string(),
        trim(),
        maxLength(MAX_CHARACTERS_SM, errorTranslation(keyFromSelector(($) => $.VALIDATION_MAX_LENGTH))),
      ),
    ),
    vatApplicability: picklist(VAT_APPLICABILITIES),
    spanishStripeAccount: boolean(),
  }),
  forward(
    partialCheck(
      [["spanishStripeAccount"], ["vatApplicability"], ["vat"]],
      ({ spanishStripeAccount, vatApplicability, vat }) =>
        !spanishStripeAccount || vatApplicability === "notApplicable" || !isEmpty(vat),
      notEmptyMessage(),
    ),
    ["vat"],
  ),
);

type BillingInformationFormData = InferOutput<typeof BillingInformationSchema>;

const toFormData = (billingProfile: BillingProfileDto): BillingInformationFormData => ({
  companyName: billingProfile.companyName ?? "",
  email: billingProfile.billingEmailAddress ?? "",
  phoneNumber: billingProfile.phoneNumber ?? "",
  address: billingProfile.billingAddress.address ?? "",
  zipCode: billingProfile.billingAddress.zipCode ?? "",
  city: billingProfile.billingAddress.city ?? "",
  countryCode: toCountryCode(billingProfile),
  vat: billingProfile.vatNumber ?? "",
  vatApplicability: "unknown",
  spanishStripeAccount: isSpanishStripeAccount(billingProfile.stripeAccount),
});

const toBillingProfileDto = (formData: BillingInformationFormData): BillingProfilePatchDto => ({
  companyName: formData.companyName,
  billingEmailAddress: formData.email,
  phoneNumber: formData.phoneNumber,
  // The backend ignores VAT numbers outside the EU and can't clear one, so only send it when it applies
  vatNumber: formData.vatApplicability !== "notApplicable" && !isEmpty(formData.vat) ? formData.vat : undefined,
  billingAddress: {
    address: formData.address,
    zipCode: formData.zipCode,
    city: formData.city,
    countryCode: formData.countryCode,
  },
});

const isInvalidVatError = (error: unknown): boolean =>
  error instanceof MoreAppError &&
  error.status === HttpStatusCode.BadRequest &&
  INVALID_VAT_MESSAGE_PATTERN.test(error.message);

type BillingInformationSectionProps = {
  billingProfile: BillingProfileDto;
};

const BillingInformationSection: VoidComponent<BillingInformationSectionProps> = (props) => {
  const { t } = useTranslation();
  const { open } = useModalManager();
  const patchBillingProfile = BillingService.patchBillingProfile();

  const [editing, setEditing] = createSignal(false);

  const form = createForm<BillingInformationFormData>(
    BillingInformationSchema,
    async (formData) => {
      await pMinDelay(patchBillingProfile.mutateAsync(toBillingProfileDto(formData)), MINIMUM_ACTION_LOADING_DELAY)
        .then(() => setEditing(false))
        .catch((error: unknown) => {
          if (isInvalidVatError(error)) {
            form.updateError(
              "vat",
              t(($) => $.SETTINGS_BILLING_BILLING_INFORMATION_VAT_INVALID),
            );
          } else {
            open({ content: () => <GenericFailureModal error={error} /> });
          }
        });
    },
    toFormData(props.billingProfile),
  );

  const countryQuery = CountriesService.getCountry(() => form.getValue("countryCode"));
  // Only read the data once loaded, as reading it while loading suspends the whole page
  const vatApplicability = createMemo((): VatApplicability => {
    if (!countryQuery.isSuccess) {
      return "unknown";
    }
    return isEuCountry(countryQuery.data) ? "applicable" : "notApplicable";
  });

  // The schema needs to know whether the VAT number applies, so keep it in sync as a form value. It is also set as
  // initial value, as it isn't a change made by the user.
  createEffect(() => {
    const applicability = vatApplicability();
    if (form.getValue("vatApplicability") !== applicability) {
      form.updateValue("vatApplicability", applicability);
      form.setInitialValue("vatApplicability", applicability);
    }
  });

  const isSpanishCustomer = (): boolean => isSpanishStripeAccount(props.billingProfile.stripeAccount);

  const vatLabel = (): string =>
    isSpanishCustomer()
      ? t(($) => $.SETTINGS_BILLING_BILLING_INFORMATION_VAT_NIF_LABEL)
      : t(($) => $.SETTINGS_BILLING_BILLING_INFORMATION_VAT_LABEL);

  const isVatShown = (): boolean =>
    editing() ? vatApplicability() !== "notApplicable" : !isEmpty(props.billingProfile.vatNumber);

  const startEditing = (): void => {
    form.reset(toFormData(props.billingProfile));
    setEditing(true);
  };

  return (
    <SettingSection
      ariaIdPrefix="billing"
      title={t(($) => $.SETTINGS_BILLING_BILLING_INFORMATION_TITLE)}
      description={t(($) => $.SETTINGS_BILLING_BILLING_INFORMATION_DESCRIPTION)}
    >
      <div class="flex min-w-0 grow flex-col gap-6">
        <EditableSettingTextValue
          editing={editing()}
          label={t(($) => $.SETTINGS_BILLING_BILLING_INFORMATION_COMPANY_NAME_LABEL)}
          text={props.billingProfile.companyName ?? ""}
          editValue={form.getValue("companyName")}
          editError={form.getError("companyName")}
          editDisabled={form.isSubmitting()}
          editOnBlur={(value) => form.updateValue("companyName", value)}
          required
        />

        <div class="flex gap-6">
          <div class="min-w-0 flex-1">
            <EditableSettingTextValue
              editing={editing()}
              label={t(($) => $.SETTINGS_BILLING_BILLING_INFORMATION_EMAIL_LABEL)}
              text={props.billingProfile.billingEmailAddress ?? ""}
              editValue={form.getValue("email")}
              editError={form.getError("email")}
              editDisabled={form.isSubmitting()}
              editOnBlur={(value) => form.updateValue("email", value)}
              required
            />
          </div>
          <div class="min-w-0 flex-1">
            <EditablePhoneNumberValue
              editing={editing()}
              label={t(($) => $.SETTINGS_BILLING_BILLING_INFORMATION_PHONE_NUMBER_LABEL)}
              phoneNumber={props.billingProfile.phoneNumber ?? ""}
              editValue={form.getValue("phoneNumber")}
              editError={form.getError("phoneNumber")}
              editDisabled={form.isSubmitting()}
              editOnChange={(value) => form.updateValue("phoneNumber", value)}
              required
            />
          </div>
        </div>

        <div class="flex gap-6">
          <div class="min-w-0 flex-1">
            <EditableSettingTextValue
              editing={editing()}
              label={t(($) => $.SETTINGS_BILLING_BILLING_INFORMATION_ADDRESS_LABEL)}
              text={props.billingProfile.billingAddress.address ?? ""}
              editValue={form.getValue("address")}
              editError={form.getError("address")}
              editDisabled={form.isSubmitting()}
              editOnBlur={(value) => form.updateValue("address", value)}
              required
            />
          </div>
          <div class="min-w-0 flex-1">
            <EditableSettingTextValue
              editing={editing()}
              label={t(($) => $.SETTINGS_BILLING_BILLING_INFORMATION_ZIP_CODE_LABEL)}
              text={props.billingProfile.billingAddress.zipCode ?? ""}
              editValue={form.getValue("zipCode")}
              editError={form.getError("zipCode")}
              editDisabled={form.isSubmitting()}
              editOnBlur={(value) => form.updateValue("zipCode", value)}
              required
            />
          </div>
        </div>
        <div class="flex gap-6">
          <div class="min-w-0 flex-1">
            <EditableCountryValue
              editing={editing()}
              label={t(($) => $.SETTINGS_BILLING_BILLING_INFORMATION_COUNTRY_LABEL)}
              countryCode={toCountryCode(props.billingProfile)}
              fallbackName={props.billingProfile.billingAddress.country ?? ""}
              editValue={form.getValue("countryCode")}
              editError={form.getError("countryCode")}
              editDisabled={form.isSubmitting()}
              editOnSelect={(value) => form.updateValue("countryCode", value)}
              required
            />
          </div>
          <div class="min-w-0 flex-1">
            <EditableSettingTextValue
              editing={editing()}
              label={t(($) => $.SETTINGS_BILLING_BILLING_INFORMATION_CITY_LABEL)}
              text={props.billingProfile.billingAddress.city ?? ""}
              editValue={form.getValue("city")}
              editError={form.getError("city")}
              editDisabled={form.isSubmitting()}
              editOnBlur={(value) => form.updateValue("city", value)}
              required
            />
          </div>
        </div>
        <Show when={isVatShown()}>
          <EditableSettingTextValue
            editing={editing()}
            label={vatLabel()}
            text={props.billingProfile.vatNumber ?? ""}
            editValue={form.getValue("vat")}
            editError={form.getError("vat")}
            editDisabled={form.isSubmitting()}
            editOnBlur={(value) => form.updateValue("vat", value)}
            required={isSpanishCustomer()}
          />
        </Show>

        <SettingSectionEditActions
          editing={editing()}
          form={form}
          cancelLabel={t(($) => $.SETTINGS_BILLING_BILLING_INFORMATION_CANCEL_BUTTON)}
          saveLabel={t(($) => $.SETTINGS_BILLING_BILLING_INFORMATION_SAVE_BUTTON)}
          savingLabel={t(($) => $.SETTINGS_BILLING_BILLING_INFORMATION_SAVING_BUTTON)}
          stopEditing={() => setEditing(false)}
          saveDisabled={countryQuery.isFetching || countryQuery.isPlaceholderData}
        />
      </div>

      <SettingSectionEditButton
        editing={editing()}
        editLabel={t(($) => $.SETTINGS_BILLING_BILLING_INFORMATION_EDIT_BUTTON)}
        setEditing={startEditing}
      />
    </SettingSection>
  );
};

export default BillingInformationSection;
